import variantCodes from "@/utils/product-variant-codes";
import productDetailsCodes from "@/utils/product-details-codes";
import commercialStickerCodes from "@/utils/commercial-sticker-codes";
import { useInteractionDoneTracking } from "@/mixins/tracking/interaction-done";
import {
  commercialPictos,
  commercialPictosTypes
} from "@/utils/picto-commercial";

const AddToPurchaseListComponent = defineAsyncComponent(() =>
  import("@/components/organisms/product-sheet/add-to-purchase-list")
);

export const productDetailsProps = () => ({
  photos: {
    type: Array
  },
  quantity: {
    type: Number,
    required: false
  },
  selectedPftPlageIndex: {
    type: Number,
    required: false
  },
  shopName: {
    type: String,
    required: true
  },
  isNational: {
    type: Boolean,
    required: true
  },
  isPro: {
    type: Boolean,
    required: true
  },
  product: {
    type: Object,
    required: true
  },
  aliasWeb: {
    type: String,
    required: false
  },
  shopId: {
    type: Number,
    required: true
  },
  firstLevelBreadcrumb: {
    type: Object,
    required: false
  },
  breadcrumb: {
    type: Array,
    required: true
  },
  shopHasLdd: {
    type: Boolean,
    required: false,
    default: false
  },
  shopHasClickAndCollect: {
    type: Boolean,
    required: false,
    default: false
  },
  shopHasClickAndCollectTraiteur: {
    type: Boolean,
    required: false,
    default: false
  },
  shopHasClickAndCollectTraiteurNoel: {
    type: Boolean,
    required: false,
    default: false
  },
  isReplacedByNational: {
    type: Boolean,
    required: false,
    default: false
  },
  isPopin: {
    type: Boolean,
    required: false,
    default: false
  },
  fromProspectus: {
    type: Boolean,
    required: false,
    default: false
  }
});

export const productDetailsEmits = () => [
  "setQuantity",
  "addToCartPft",
  "indexPoidsChanged",
  "removeItem"
];

export const useProductDetails = (props, context) => {
  const { trackEvent } = useInteractionDoneTracking();
  const pftSelected = ref(false);
  const indexPoids = ref(0);
  const showPlanningClickCollectTraiteur = ref(false);
  const showPlanningClickCollectTraiteurNoel = ref(false);
  const prixCKDO = ref(null);

  const isPrimeMarche = computed(() => {
    return !!props.product.attributes?.avantages?.find(
      (avantage) => avantage.prime_fid?.type === "CLUB_MARCHE"
    );
  });

  const isPrimeBio = computed(() => {
    return !!props.product.attributes?.avantages?.find(
      (avantage) => avantage.prime_fid?.type === "CLUB_BIO"
    );
  });

  const isMardiPass = computed(() => {
    return !!props.product.attributes?.avantages?.find(
      (avantage) => avantage.prime_fid?.type === "PASS_DAY"
    );
  });

  const getIconPrime = computed(() => {
    if (isMardiPass.value) {
      return "carte-pass-carrefour";
    } else {
      return "carte-club-carrefour";
    }
  });

  const AddToPurchaseList = computed(() => {
    return AddToPurchaseListComponent;
  });
  const dpClickAndCollectTraiteurUrl = computed(() => {
    return useRuntimeConfig().public.TRAITEUR_PERMANENT_CATEGORY_SLUG;
  });
  const dpClickAndCollectTraiteurNoelUrl = computed(() => {
    return useRuntimeConfig().public.TRAITEUR_DE_FETES_CATEGORY_SLUG;
  });
  const isBuffet = computed(() => {
    return !!props.product.attributes.qte_pas;
  });
  const isPft = computed(() => {
    let isPft = false;
    if (
      props.product.attributes.pft === 1 &&
      props.product.attributes.pft_conf
    ) {
      isPft = true;
    }
    return isPft;
  });
  const amount = computed(() => {
    if (isPft.value) {
      return props.product.attributes.pft_conf.PLAGE[indexPoids.value].PRIX;
    }
    return formattedPrice(props.product.attributes.prix);
  });
  const discountedAmount = computed(() => {
    if (isPft.value) {
      return props.product.attributes.pft_conf.PLAGE[indexPoids.value]
        .PRIX_BARRE;
    }
    return props.product.attributes.prix_barre;
  });
  const discountRate = computed(() => {
    if (isPft.value) {
      return props.product.attributes.pft_conf.PLAGE[indexPoids.value]
        .P100_REDUC &&
        props.product.attributes.pft_conf.PLAGE[0].P100_REDUC !== "0.00"
        ? props.product.attributes.pft_conf.PLAGE[0].P100_REDUC
        : null;
    }
    return props.product.attributes.discount_percent !== "0.00"
      ? props.product.attributes.discount_percent
      : null;
  });
  const pricePerUnit = computed(() => {
    return props.product.attributes.pft === 1 &&
      props.product.attributes.pft_conf &&
      props.product.attributes.pft_conf.CONF.PRIX100G
      ? (props.product.attributes.prix_unite_reference / 10).toFixed(2)
      : props.product.attributes.prix_unite_reference;
  });
  const uniteRef = computed(() => {
    let uniteRef = props.product.attributes.unite_reference;
    if (uniteRef) {
      uniteRef = uniteRef.substr(uniteRef.indexOf(" ") + 1, uniteRef.length);
    }
    return uniteRef;
  });
  const characteristicsStickers = computed(() => {
    let characteristicsStickers = [];
    if (
      props.product.attributes.pictos_caracteristiques !== null &&
      props.product.attributes.pictos_caracteristiques.length > 0
    ) {
      characteristicsStickers =
        props.product.attributes.pictos_caracteristiques.filter(
          (e) =>
            e.code !== "non_lad" &&
            e.code !== "non_lee" &&
            e.code !== "non_prc" &&
            e.code !== "non_prc_delipop"
        );
    }
    return characteristicsStickers;
  });
  const showGarantieLegale = computed(() => {
    if (
      !props.product.attributes.description &&
      props.product.attributes.garantie_legale
    ) {
      return true;
    }
    return (
      props.product.attributes.description &&
      props.product.attributes.description.length <
        productDetailsCodes.DESCRIPTON_MAX_LENGTH &&
      props.product.attributes.garantie_legale
    );
  });
  const isCarteKdo = computed(() => {
    return props.product.attributes.type === "CKDO";
  });
  const isClickAndCollectTraiteur = computed(() => {
    return props.product.attributes.types_panier.includes(
      "click_and_collect_traiteur_permanent"
    );
  });
  const isClickAndCollectTraiteurNoel = computed(() => {
    return props.product.attributes.types_panier.includes(
      "click_and_collect_traiteur_noel"
    );
  });
  const isClickAndCollectOnly = computed(() => {
    return (
      props.product.attributes.types_panier.includes("click_and_collect") &&
      props.product.attributes.types_panier.length === 1
    );
  });
  const isLdd = computed(() => {
    return (
      props.product.attributes.types_panier &&
      props.product.attributes.types_panier.includes("ldd")
    );
  });
  const isLddOnly = computed(() => {
    return isLdd.value && props.product.attributes.types_panier.length === 1;
  });
  const variantEnStock = computed(() => {
    return props.product.attributes.surcharge
      ? props.product.attributes.surcharge.en_stock
      : props.product.attributes.en_stock;
  });
  const isNotEligibleClickAndCollect = computed(() => {
    return (
      ((props.isPro &&
        (isClickAndCollectOnly.value ||
          isClickAndCollectTraiteur.value ||
          isClickAndCollectTraiteurNoel.value)) ||
        (isClickAndCollectOnly.value && !props.shopHasClickAndCollect) ||
        (isClickAndCollectTraiteur.value &&
          !props.shopHasClickAndCollectTraiteur) ||
        (isClickAndCollectTraiteurNoel.value &&
          !props.shopHasClickAndCollectTraiteurNoel)) &&
      !props.isNational
    );
  });
  const isNotEligibleLdd = computed(() => {
    return (
      (props.isPro && isLddOnly.value) ||
      (isLddOnly.value && !props.shopHasLdd && !props.isNational)
    );
  });

  const showShippingMethodEligibility = computed(() => {
    return (
      !props.isNational &&
      ((props.product.attributes.unavailable_services &&
        props.product.attributes.unavailable_services.length > 0) ||
        (!!props.product.attributes.jours_retraits &&
          !!props.product.attributes.jours_retraits.find(
            (jour) => !jour.disponible
          )))
    );
  });

  const setQuantity = (qty) => {
    context.emit("setQuantity", qty);
  };
  const addToCartPft = (index) => {
    context.emit("addToCartPft", index);
  };
  const indexPoidsChanged = ({ index, type }) => {
    indexPoids.value = index;
    context.emit("indexPoidsChanged", { index, type });
  };
  const removeItem = () => {
    context.emit("removeItem");
  };
  const formattedPrice = (price) => {
    const fixedAmount = price ? parseFloat(price).toFixed(2) : price;
    return fixedAmount;
  };
  const getNutriScoreClassName = () => {
    if (
      (characteristicsStickers.value &&
        characteristicsStickers.value.length > 0) ||
      props.product.attributes.origine
    ) {
      return "c-product-detail__nutriscore";
    } else {
      return "c-product-detail__nutriscore__first_picto";
    }
  };
  const url = (code) => {
    try {
      if (!code || !commercialPictosTypes.find((item) => code === item)) {
        return null;
      }
      return commercialPictos[code];
    } catch (error) {
      return null;
    }
  };

  const trackingCtaMarque = () => {
    if (props.product.attributes.url_marque) {
      trackEvent({
        categoryGA: trackingCodes.CATEGORYGA.ficheProduit,
        actionGA: trackingCodes.ACTIONGA.ctaMarque,
        labelGA: props.product.attributes.marque
      });
    }
  };

  return {
    pftSelected,
    indexPoids,
    showPlanningClickCollectTraiteur,
    showPlanningClickCollectTraiteurNoel,
    AddToPurchaseList,
    dpClickAndCollectTraiteurUrl,
    dpClickAndCollectTraiteurNoelUrl,
    isPft,
    amount,
    discountedAmount,
    discountRate,
    pricePerUnit,
    uniteRef,
    characteristicsStickers,
    showGarantieLegale,
    isClickAndCollectTraiteur,
    isClickAndCollectTraiteurNoel,
    isClickAndCollectOnly,
    isLdd,
    isLddOnly,
    variantEnStock,
    isNotEligibleClickAndCollect,
    isNotEligibleLdd,
    showShippingMethodEligibility,
    setQuantity,
    isBuffet,
    addToCartPft,
    indexPoidsChanged,
    removeItem,
    formattedPrice,
    getNutriScoreClassName,
    url,
    trackingCtaMarque,
    variantCodes,
    productDetailsCodes,
    commercialStickerCodes,
    prixCKDO,
    isCarteKdo,
    isPrimeMarche,
    isMardiPass,
    isPrimeBio,
    getIconPrime
  };
};
